import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, MapPin, Star, Clock, ChevronRight, ShoppingBag, Plus, Minus, 
  Heart, Filter, Bike, X, Check, Sparkles, Percent, Gift
} from 'lucide-react';

const restaurants = [
  {
    id: 1,
    name: 'Burger Palace',
    image: '🍔',
    rating: 4.9,
    reviews: 2847,
    time: '15-25',
    distance: '0.8 km',
    tags: ['Burgers', 'American', 'Fast Food'],
    featured: true,
    aiRecommended: true,
    promo: '20% off',
    deliveryFee: 'Free'
  },
  {
    id: 2,
    name: 'Sakura Sushi',
    image: '🍣',
    rating: 4.8,
    reviews: 1923,
    time: '25-35',
    distance: '1.5 km',
    tags: ['Sushi', 'Japanese', 'Healthy'],
    featured: false,
    promo: null,
    deliveryFee: '$2.99'
  },
  {
    id: 3,
    name: 'Pizza Napoletana',
    image: '🍕',
    rating: 4.7,
    reviews: 3241,
    time: '20-30',
    distance: '1.2 km',
    tags: ['Pizza', 'Italian'],
    featured: false,
    promo: 'Buy 1 Get 1',
    deliveryFee: '$1.99'
  },
];

const categories = [
  { id: 'all', label: 'All', icon: '🍽️' },
  { id: 'burger', label: 'Burgers', icon: '🍔' },
  { id: 'pizza', label: 'Pizza', icon: '🍕' },
  { id: 'sushi', label: 'Sushi', icon: '🍣' },
  { id: 'salad', label: 'Healthy', icon: '🥗' },
  { id: 'dessert', label: 'Desserts', icon: '🍰' },
];

export default function FoodPreview({ customization }) {
  const theme = customization?.theme || { primary: '#ef4444', secondary: '#f97316', gradient: 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)' };
  const companyName = customization?.companyName || 'FoodDash';
  
  const [activeCategory, setActiveCategory] = useState('all');
  const [cartCount, setCartCount] = useState(2);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [favorites, setFavorites] = useState([1]);

  const toggleFavorite = (id, e) => {
    e.stopPropagation();
    setFavorites(prev => 
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    setCartCount(prev => prev + 1);
  };

  return (
    <div style={{ 
      height: '100%', 
      background: '#fff',
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Status Bar */}
      <div style={{ 
        padding: '8px 16px', 
        display: 'flex', 
        justifyContent: 'space-between',
        fontSize: '11px',
        color: '#1f2937',
        background: '#fff'
      }}>
        <span style={{ fontWeight: '500' }}>9:41</span>
        <div style={{ display: 'flex', gap: '4px' }}>
          <span>📶</span>
          <span>🔋</span>
        </div>
      </div>

      {/* Header */}
      <div style={{ padding: '8px 16px 14px', background: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
              <MapPin size={14} color={theme.primary} />
              <span style={{ fontSize: '11px', color: '#6b7280' }}>Deliver to</span>
              <ChevronRight size={12} color="#6b7280" />
            </div>
            <p style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
              Karl Johans gate 25 🏠
            </p>
          </div>
          <motion.div 
            whileTap={{ scale: 0.95 }}
            style={{
              position: 'relative',
              padding: '10px',
              background: `linear-gradient(135deg, ${theme.primary}15, ${theme.primary}08)`,
              borderRadius: '14px',
              cursor: 'pointer'
            }}
          >
            <ShoppingBag size={22} color={theme.primary} />
            {cartCount > 0 && (
              <motion.div 
                key={cartCount}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                style={{
                  position: 'absolute',
                  top: '4px', right: '4px',
                  width: '18px', height: '18px',
                  background: theme.gradient,
                  borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '10px', color: 'white', fontWeight: '700',
                  boxShadow: `0 2px 8px ${theme.primary}60`
                }}
              >
                {cartCount}
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Search Bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          background: '#f3f4f6',
          borderRadius: '14px',
          padding: '12px 16px'
        }}>
          <Search size={18} color="#9ca3af" />
          <input
            type="text"
            placeholder={`Search on ${companyName}...`}
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              outline: 'none',
              fontSize: '14px',
              color: '#1f2937'
            }}
          />
          <div style={{ 
            width: '1px', height: '20px', background: '#d1d5db' 
          }} />
          <Filter size={18} color="#6b7280" style={{ cursor: 'pointer' }} />
        </div>
      </div>

      {/* Categories */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        padding: '0 16px 14px',
        overflowX: 'auto',
        background: 'white'
      }}>
        {categories.map((cat) => (
          <motion.button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '10px 16px',
              background: activeCategory === cat.id 
                ? theme.gradient 
                : '#f9fafb',
              border: 'none',
              borderRadius: '14px',
              fontSize: '12px',
              fontWeight: '500',
              color: activeCategory === cat.id ? 'white' : '#4b5563',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: activeCategory === cat.id 
                ? `0 4px 15px ${theme.primary}40` 
                : 'none'
            }}
          >
            <span>{cat.icon}</span>
            {cat.label}
          </motion.button>
        ))}
      </div>

      {/* AI Recommendation Banner */}
      <div style={{ padding: '0 16px 14px' }}>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: `linear-gradient(135deg, ${theme.primary}15 0%, ${theme.secondary || theme.primary}10 100%)`,
            borderRadius: '16px',
            padding: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 4px 15px rgba(251, 191, 36, 0.1)',
            border: `1px solid ${theme.primary}20`
          }}
        >
          <motion.div 
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{ fontSize: '32px' }}
          >
            🤖
          </motion.div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: theme.primary }}>
                {companyName} AI Picks for You
              </p>
              <Sparkles size={12} color={theme.primary} />
            </div>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#71717a' }}>
              Based on your taste: Try Burger Palace's new Truffle Smash!
            </p>
          </div>
          <motion.button
            onClick={handleAddToCart}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '8px 14px',
              background: theme.gradient,
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: `0 4px 12px ${theme.primary}40`
            }}
          >
            Order
          </motion.button>
        </motion.div>
      </div>

      {/* Restaurant List */}
      <div style={{ flex: 1, padding: '0 16px', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
            Nearby Restaurants
          </h3>
          <span style={{ fontSize: '11px', color: '#6b7280' }}>
            {restaurants.length} places
          </span>
        </div>
        
        {restaurants.map((restaurant, i) => (
          <motion.div
            key={restaurant.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            whileTap={{ scale: 0.98 }}
            whileHover={{ x: 4 }}
            onClick={() => setSelectedRestaurant(restaurant.id)}
            style={{
              display: 'flex',
              gap: '14px',
              padding: '14px',
              background: selectedRestaurant === restaurant.id ? `${theme.primary}08` : '#f9fafb',
              borderRadius: '18px',
              marginBottom: '12px',
              cursor: 'pointer',
              border: selectedRestaurant === restaurant.id ? `2px solid ${theme.primary}30` : '2px solid transparent',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.2s'
            }}
          >
            {/* Promo badge */}
            {restaurant.promo && (
              <div style={{
                position: 'absolute',
                top: '12px', left: '12px',
                background: theme.gradient,
                color: 'white',
                fontSize: '9px',
                fontWeight: '700',
                padding: '4px 8px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                zIndex: 1
              }}>
                <Percent size={10} />
                {restaurant.promo}
              </div>
            )}
            
            <div style={{
              width: '85px', height: '85px',
              borderRadius: '16px',
              background: `linear-gradient(135deg, ${theme.primary}15 0%, ${theme.primary}05 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '40px',
              flexShrink: 0
            }}>
              {restaurant.image}
            </div>
            
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '6px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                      {restaurant.name}
                    </h4>
                    {restaurant.aiRecommended && (
                      <span style={{
                        fontSize: '8px', padding: '3px 6px',
                        background: `linear-gradient(135deg, ${theme.primary}20, ${theme.primary}10)`,
                        color: theme.primary,
                        borderRadius: '6px', fontWeight: '600',
                        display: 'flex', alignItems: 'center', gap: '3px'
                      }}>
                        🤖 AI Pick
                      </span>
                    )}
                  </div>
                </div>
                <motion.button
                  onClick={(e) => toggleFavorite(restaurant.id, e)}
                  whileTap={{ scale: 0.8 }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                >
                  <Heart 
                    size={18} 
                    color={favorites.includes(restaurant.id) ? theme.primary : '#d1d5db'}
                    fill={favorites.includes(restaurant.id) ? theme.primary : 'none'}
                  />
                </motion.button>
              </div>
              
              <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                {restaurant.tags.map((tag) => (
                  <span key={tag} style={{
                    fontSize: '10px', padding: '3px 8px',
                    background: '#e5e7eb', color: '#6b7280',
                    borderRadius: '8px'
                  }}>
                    {tag}
                  </span>
                ))}
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '11px', color: '#6b7280' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Star size={12} fill="#f59e0b" color="#f59e0b" />
                  <span style={{ fontWeight: '600', color: '#1f2937' }}>{restaurant.rating}</span>
                  <span>({restaurant.reviews})</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Clock size={12} />
                  {restaurant.time} min
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Bike size={12} />
                  <span style={{ 
                    color: restaurant.deliveryFee === 'Free' ? theme.primary : '#6b7280',
                    fontWeight: restaurant.deliveryFee === 'Free' ? '600' : '400'
                  }}>
                    {restaurant.deliveryFee}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Bottom Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-around',
        padding: '14px 0 8px',
        borderTop: '1px solid #e5e7eb',
        background: 'white'
      }}>
        {[
          { icon: '🏠', label: 'Home', active: true },
          { icon: '🔍', label: 'Browse' },
          { icon: '💳', label: 'Orders' },
          { icon: '❤️', label: 'Saved' },
          { icon: '👤', label: 'Profile' }
        ].map((item, i) => (
          <motion.div 
            key={item.label}
            whileTap={{ scale: 0.9 }}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              opacity: item.active ? 1 : 0.5,
              cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: '20px' }}>{item.icon}</span>
            <span style={{ 
              fontSize: '10px', marginTop: '4px', 
              color: item.active ? theme.primary : '#6b7280',
              fontWeight: item.active ? '600' : '400'
            }}>
              {item.label}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
